import {
  Signal,
  BookOpen,
  Video,
  ListChecks,
  Calendar,
  Circle,
  Clock,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import StatCard from "../components/StatCard";
import ProgressBar from "../components/ProgressBar";
import Badge from "../components/Badge";
import LoadingSkeleton from "../components/LoadingSkeleton";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "../lib/useQuery";
import {
  getStudent,
  getActivities,
  getPayments,
  getSessions,
} from "../lib/queries";
import { MONTHS_SHORT, PROGRAM_WEEKS } from "../lib/constants";

function getWeeksLeft(startDate) {
  if (!startDate) return PROGRAM_WEEKS;
  const elapsed = (Date.now() - new Date(startDate).getTime()) / (7 * 24 * 60 * 60 * 1000);
  return Math.max(0, Math.ceil(PROGRAM_WEEKS - elapsed));
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data: student, loading: l1 } = useQuery(
    () => getStudent(user.id), [user.id]
  );
  const { data: activities, loading: l2 } = useQuery(
    () => getActivities(user.id), [user.id]
  );
  const { data: payments, loading: l3 } = useQuery(
    () => getPayments(user.id), [user.id]
  );
  const { data: sessions, loading: l4 } = useQuery(
    () => getSessions(user.id), [user.id]
  );

  // Solo esperar datos de Supabase (rápidos)
  if (l1 || l2) return <LoadingSkeleton />;
  if (!student) return null;

  const loadingAirtable = l3 || l4;

  const completedActivities =
    activities?.filter((a) => a.completed).length ?? 0;
  const totalActivities = activities?.length ?? 0;
  const actPercent = totalActivities
    ? Math.round((completedActivities / totalActivities) * 100)
    : 0;

  const completedSessions =
    sessions?.filter((s) => s.status === "completed").length ?? 0;
  const weeksLeft = getWeeksLeft(student.start_date);

  const nextSession = sessions?.find((s) => s.status === "scheduled");
  const nextDate = nextSession ? new Date(nextSession.date) : null;

  const allPending = (activities ?? []).filter((a) => !a.completed);
  const pendingTasks = allPending.slice(0, 10);
  const hasMoreTasks = allPending.length > 10;

  return (
    <div className="animate-fade-in">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <StatCard
          icon={Signal}
          value={`${actPercent}%`}
          label="Progreso General"
          color="brand"
        >
          <ProgressBar value={actPercent} height="h-1.5" />
        </StatCard>
        <StatCard
          icon={BookOpen}
          value={`${completedActivities}/${totalActivities}`}
          label="Actividades"
          color="green"
        />
        <StatCard
          icon={Video}
          value={loadingAirtable ? "..." : `${completedSessions}`}
          label="Sesiones realizadas"
          color="blue"
        />
        <StatCard
          icon={Clock}
          value={`${weeksLeft}`}
          label="Semanas restantes"
          color="amber"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <ListChecks size={18} className="text-brand" />
              Próximos Pasos
            </h3>
            <Badge variant="info">{student.stage}</Badge>
          </div>
          <div className="p-4 md:p-6 space-y-1">
            {pendingTasks.length > 0 ? (
              <>
                {pendingTasks.map((a) => (
                  <Link
                    key={a.id}
                    to="/actividades"
                    className="flex items-center gap-3 py-2.5 px-3 rounded-lg bg-brand-light/60 hover:bg-brand-light transition-colors"
                  >
                    <Circle size={18} className="text-gray-300 shrink-0" />
                    <span className="text-sm text-gray-700 font-medium">
                      {a.name}
                    </span>
                  </Link>
                ))}
                <Link
                  to="/actividades"
                  className="block text-center text-sm text-brand hover:text-brand-dark font-medium pt-3"
                >
                  {hasMoreTasks ? `Ver todas (${allPending.length})` : "Ver todas"}
                </Link>
              </>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Todas las actividades completadas
              </p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {loadingAirtable ? (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar size={18} className="text-brand-accent" />
                  Próxima Sesión
                </h3>
              </div>
              <div className="p-6 flex items-center justify-center">
                <Loader2 size={24} className="animate-spin text-brand" />
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar size={18} className="text-brand-accent" />
                  Próxima Sesión
                </h3>
              </div>
              {nextSession && nextDate ? (
                <div className="p-6 text-center">
                  <div className="text-4xl font-extrabold text-brand">
                    {nextDate.getDate()} {MONTHS_SHORT[nextDate.getMonth()]}
                  </div>
                  <div className="text-sm font-semibold mt-1">
                    {nextSession.type}
                  </div>
                  <div className="text-xs text-gray-500">
                    {nextSession.time}h · con {nextSession.mentor}
                  </div>
                  {nextSession.meeting_url ? (
                    <a
                      href={nextSession.meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 px-5 py-2 bg-brand hover:bg-brand-dark text-white text-sm font-medium rounded-lg transition-all flex items-center gap-2 mx-auto w-fit"
                    >
                      <Video size={15} />
                      Unirse
                      <ExternalLink size={12} />
                    </a>
                  ) : (
                    <p className="mt-4 text-xs text-gray-400">
                      El enlace estará disponible próximamente
                    </p>
                  )}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <p className="text-sm text-gray-500">No hay sesiones programadas</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
